import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Eye } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { Empty, ErrorBlock, Img, Price, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { useCaseStyles, useCases } from "../../api/queries.ts";
import { useSpaces } from "../../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 装修案例
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **案例是拿来"翻"的，所以网页上排成三列大图。**
 *    手机版是一张接一张全宽大图竖着排 —— 窄屏上只能这样，但也意味着
 *    一屏只看得到一套半。案例的作用是让人**在几套里挑一套像自己家的**，
 *    看不到几套就挑不起来。
 *
 * ⚠️ 空间 / 风格两组筛选并排放在顶部一条里：手机上是两行横滑条，
 *    网页上一行就够，而且两组条件的关系（是"且"）一眼看得出来。
 */

/*
  ⚠️ 空间这一排以前在这儿又写了一份，而且**漏了阳台** ——
     后台给案例打了「阳台」标签，前台却没有那一格，等于打了也筛不到。
     现在跟首页共用后台配的那份，前面补一个「全部」。
*/
export function DesktopCases() {
  const search = useSearch({ from: "/cases" }) as { space?: string; style?: string };
  const navigate = useNavigate();
  const list = useCases({ space: search.space, style: search.style });
  const styles = useCaseStyles();
  const spaceTabs = [{ key: "", label: "全部" }, ...useSpaces()];

  const go = (next: { space?: string; style?: string }) =>
    navigate({ to: "/cases", search: { ...search, ...next } as never });

  return (
    <DesktopShell>
      <Crumbs items={[{ label: "首页", to: "/" }, { label: "装修案例" }]} />

      <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-border bg-card px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="text-[13px] text-muted-foreground">空间</span>
          {spaceTabs.map((s) => (
            <button
              key={s.key}
              onClick={() => go({ space: s.key || undefined })}
              className={cn(
                "rounded-lg px-3 py-1.5 text-[14px] transition-colors",
                (search.space ?? "") === s.key
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* 风格是从已发布的案例里现取的，没案例时这一组自动消失。 */}
        {styles.data && styles.data.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] text-muted-foreground">风格</span>
            <button
              onClick={() => go({ style: undefined })}
              className={cn(
                "rounded-lg px-3 py-1.5 text-[14px] transition-colors",
                !search.style
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              不限
            </button>
            {styles.data.map((st) => (
              <button
                key={st}
                onClick={() => go({ style: st })}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-[14px] transition-colors",
                  search.style === st
                    ? "bg-brand-600 text-white"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                )}
              >
                {st}
              </button>
            ))}
          </div>
        )}
      </div>

      {list.isPending ? (
        <Spinner compact />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="还没有案例" hint="平台正在整理，先去看看商品吧" compact />
      ) : (
        <div className="grid grid-cols-3 gap-6">
          {list.data.items.map((c) => (
            <Link
              key={c.id}
              to="/cases/$slug"
              params={{ slug: c.slug }}
              className="group overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-brand-200 hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)]"
            >
              {/* ⚠️ 用 Img 而不是裸 img：图挂了要退回灰底占位，破图比空白还难看。 */}
              <div className="aspect-[4/3] overflow-hidden bg-secondary">
                <Img
                  src={c.coverUrl}
                  alt={c.title}
                  className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-4">
                <p className="line-clamp-1 text-[16px] font-medium">{c.title}</p>
                <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-muted-foreground">
                  {c.style && <span>{c.style}</span>}
                  {c.areaSqm !== null && <span>{c.areaSqm} ㎡</span>}
                  {c.budget !== null && (
                    <span>
                      预算 <Price cents={c.budget} />
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1">
                    <Eye className="size-3.5" />
                    {c.viewCount}
                  </span>
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </DesktopShell>
  );
}
